import React from 'react'
import type { PricingContent } from './nexusLandingContent'

type PricingSectionProps = {
  copy: PricingContent
}

export const PricingSection: React.FC<PricingSectionProps> = ({ copy }) => {
  return (
    <section className="landing-section" id="pricing">
      <div className="container stack-lg">
        <div className="landing-section-heading">
          <p className="landing-eyebrow">{copy.eyebrow}</p>
          <h2>{copy.title}</h2>
          <p>{copy.subtitle}</p>
          {copy.promo ? <p className="landing-proof-pill">{copy.promo}</p> : null}
        </div>

        <div className="pricing-model panel workspace-panel">
          <div className="pricing-bands" aria-label={copy.title}>
            {copy.bands.map((band) => (
              <div key={band.label} className="pricing-band">
                <span>{band.label}</span>
                <strong>{band.price}</strong>
                {band.capacity ? <em className="pricing-band-capacity">{band.capacity}</em> : null}
              </div>
            ))}
          </div>
        </div>

        {copy.overages && copy.overages.length > 0 ? (
          <div className="pricing-overages panel workspace-panel">
            <p><strong>{copy.overageTitle ?? copy.locationNote}</strong></p>
            <ul className="pricing-overage-list">
              {copy.overages.map((overage) => (
                <li key={overage.label}>
                  <span>{overage.label}</span>
                  <strong>{overage.value}</strong>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="pricing-grid">
          {copy.examples.map((example) => (
            <article key={example.exampleKey} className="pricing-card panel workspace-panel stack">
              <div>
                <h3>{example.name}</h3>
                <ul className="pricing-math">
                  {example.math.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>

              <div className="pricing-total">
                <strong>{example.total}</strong>
                <span>{example.annual}</span>
              </div>
            </article>
          ))}
        </div>

        <div className="pricing-included">
          <p><strong>{copy.includedLabel}</strong></p>
          <ul>
            {copy.features.map((feature) => (
              <li key={feature}>{feature}</li>
            ))}
          </ul>
        </div>

        {copy.implementationNote ? (
          <p className="pricing-implementation-note">{copy.implementationNote}</p>
        ) : null}

        {copy.contactCta ? (
          <div className="pricing-contact-cta">
            <a className="btn btn-primary" href={copy.contactCta.href}>{copy.contactCta.label}</a>
          </div>
        ) : null}
      </div>

      <style>{`
        .pricing-model {
          padding: 1.25rem;
        }
        .pricing-bands {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr));
          gap: 0.75rem;
        }
        .pricing-band {
          min-height: 92px;
          display: grid;
          align-content: center;
          gap: 0.35rem;
          padding: 1rem;
          border: 1px solid var(--border);
          border-radius: 0.5rem;
          background: var(--surface-muted);
        }
        .pricing-band span {
          color: var(--text-muted);
          font-weight: 700;
        }
        .pricing-band strong {
          font-size: 1.35rem;
        }
        .pricing-band-capacity {
          color: var(--text-muted);
          font-style: normal;
          font-size: 0.85rem;
          line-height: 1.3;
        }
        .pricing-overages {
          padding: 1.25rem;
        }
        .pricing-overages p {
          margin: 0 0 0.75rem;
        }
        .pricing-overage-list {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(240px, 100%), 1fr));
          gap: 0.5rem 1.5rem;
          margin: 0;
          padding: 0;
          list-style: none;
        }
        .pricing-overage-list li {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 0.75rem;
          padding: 0.5rem 0;
          border-bottom: 1px solid var(--border);
        }
        .pricing-overage-list li span {
          color: var(--text-muted);
        }
        html[dir='rtl'] .pricing-overage-list li {
          flex-direction: row-reverse;
          text-align: right;
        }
        .pricing-implementation-note {
          color: var(--text-muted);
          font-size: 0.9rem;
          max-width: 820px;
        }
        .pricing-contact-cta {
          display: flex;
          justify-content: center;
        }
        .pricing-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
          gap: 1.5rem;
          margin: 0.5rem 0;
        }
        .pricing-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 0.5rem;
          padding: 1.25rem;
        }
        .pricing-card h3 {
          margin: 0 0 0.75rem;
        }
        .pricing-math {
          display: grid;
          gap: 0.5rem;
          margin: 0;
          padding-left: 1.2rem;
          color: var(--text-muted);
        }
        html[dir='rtl'] .pricing-math {
          padding-left: 0;
          padding-right: 1.2rem;
        }
        .pricing-total {
          display: grid;
          gap: 0.35rem;
          border-top: 1px solid var(--border);
          padding-top: 1rem;
        }
        .pricing-total strong {
          font-size: 1.2rem;
        }
        .pricing-total span {
          color: var(--text-muted);
        }
        .pricing-included {
          padding: 1.5rem;
          background: var(--surface-muted);
          border: 1px solid var(--border);
          border-radius: 0.5rem;
        }
        .pricing-included p {
          margin: 0;
        }
        .pricing-included ul {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr));
          gap: 0.5rem 1rem;
          margin: 0.75rem 0 0;
          padding-left: 1.2rem;
        }
        html[dir='rtl'] .pricing-included ul {
          padding-left: 0;
          padding-right: 1.2rem;
        }
        .landing-proof-pill {
          display: inline-block;
          max-width: 820px;
          margin: 1rem auto 0;
          padding: 0.75rem 1rem;
          border: 1px solid var(--border);
          border-radius: 0.5rem;
          background: var(--surface-muted);
          font-weight: 700;
        }
      `}</style>
    </section>
  )
}
